import { Checkbox } from '@radix-ui/react-checkbox';
import { Menu } from 'lucide-react';
import { useState } from 'react';
import { TrainingTypeEnum } from '@/constants/trainingTypeOptions';
import { useRegulatoryTrainingMatrix } from '@/hooks/useNewRegulatoryTrainingMatrix';
import { NewRegulatoryTrainingData } from '@/types/NewRegulatoryTrainingData';
import SimpleMenu from '../atoms/Menu';
import DesactivateTrainingModal from '../organisms/DesactivateTrainingModal';
import TrainingModal from '../organisms/TrainingModal';
import ModalConfirmation from './ConfirmationModal';

interface NewRegulatoryTrainingMatrixHeaderProps {
	training: NewRegulatoryTrainingData;
}
const formatValue = (value: number | null, text: string) => {
	if (value === null) return ' - ';
	return ` ${value} ${text}${value > 1 ? 's' : ''}`;
};

export const NewRegulatoryTrainingMatrixHeader = ({
	training,
}: NewRegulatoryTrainingMatrixHeaderProps) => {
	const { validity, totalWorkload, recyclingWorkload } = training;
	const [isTrainingModalOpen, setIsTrainingModalOpen] = useState(false);
	const {
		massEdit: {
			selectedMassEditType,
			setSelectedMassEditType,
			handleMassEdit,
			isEditingMass,
		},
		editions: { isOnEditionMode },
		matrix: { refetch: refetchMatrix },
		permissions: {
			userIsAllowedToCheck,
			userIsAllowedToBlock,
			userIsAllowedToCreateTraining,
		},
	} = useRegulatoryTrainingMatrix();
	const [isDeactivateTrainingModalOpen, setIsDeactivateTrainingModalOpen] =
		useState(false);
	const menuOptions = [
		{
			name: 'Cadastro do Treinamento',
			onClick: () => setIsTrainingModalOpen(true),
			disabled: !userIsAllowedToCreateTraining,
		},
		{
			name: 'Desativar Treinamento',
			onClick: () => setIsDeactivateTrainingModalOpen(true),
			disabled: !userIsAllowedToCheck,
		},
		{
			name: 'Mapear Todas as Funções',
			onClick: () => setSelectedMassEditType('checkAll'),
			disabled: !isOnEditionMode || !userIsAllowedToCheck,
		},
		{
			name: 'Desmapear Todas as Funções',
			onClick: () => setSelectedMassEditType('uncheckAll'),
			disabled: !isOnEditionMode || !userIsAllowedToCheck,
		},
		{
			name: 'Bloquear a Catraca para Todas as Funções Mapeadas',
			onClick: () => setSelectedMassEditType('blockCheckAll'),
			disabled: !isOnEditionMode || !userIsAllowedToBlock,
		},
		{
			name: 'Remover Bloqueios da Catraca para Todas as Funções',
			onClick: () => setSelectedMassEditType('unblockCheckAll'),
			disabled: !isOnEditionMode || !userIsAllowedToBlock,
		},
	];
	const getModalBaseDescription = () => {
		switch (selectedMassEditType) {
			case 'checkAll':
				return 'Tem certeza que deseja mapear todas as funções?';
			case 'uncheckAll':
				return 'Tem certeza que deseja desmapear todas as funções?';
			case 'blockCheckAll':
				return 'Tem certeza que deseja bloquear a catraca para todas as funções mapeadas?';
			case 'unblockCheckAll':
				return 'Tem certeza que deseja desbloquear a catraca para todas as funções?';
			default:
				return '';
		}
	};
	return (
		<>
			{isDeactivateTrainingModalOpen && (
				<DesactivateTrainingModal
					isOpen={isDeactivateTrainingModalOpen}
					onClose={() => setIsDeactivateTrainingModalOpen(false)}
					trainingId={training.id}
					matrixRefetch={refetchMatrix}
					trainingType={TrainingTypeEnum.REGULATORY}
				/>
			)}
			{isTrainingModalOpen && (
				<TrainingModal
					isOpen={isTrainingModalOpen}
					onClose={() => setIsTrainingModalOpen(false)}
					matrixRefetch={refetchMatrix}
					trainingId={training.id}
					trainingType={TrainingTypeEnum.REGULATORY}
				/>
			)}
			{selectedMassEditType !== null && (
				<ModalConfirmation
					isOpen={selectedMassEditType !== null}
					onClose={() => setSelectedMassEditType(null)}
					confirmAction={() => handleMassEdit(training.id)}
					title="Mapeamento Massivo"
					description={`${getModalBaseDescription()} Essa ação será feita em todos os resultados do filtro atual.`}
					isLoading={isEditingMass}
				/>
			)}
			<th className="bg-[#BCDAFD] w-72 text-primary h-full border-x-2  border-white">
				<div className="flex justify-between items-center h-full">
					<div className="flex align-top gap-1">
						<div className="mt-1">
							<Checkbox />
						</div>
						<div className="w-72 flex flex-col h-full  justify-between gap-2 mx-1">
							<h5 className="font-bold text-sm text-justify">
								{training.name}
							</h5>
							<div className="text-xs font-normal flex gap-3">
								<div>
									<span>Validade:</span>
									<span>{formatValue(validity, 'ano')}</span>
								</div>
								<div>
									<span>CH:</span>
									<span>{formatValue(totalWorkload, 'hora')}</span>
								</div>
								<div>
									<span>CH REC.:</span>
									<span>{formatValue(recyclingWorkload, 'hora')}</span>
								</div>
							</div>
						</div>
					</div>
					<div>
						<button type="button">
							<SimpleMenu
								options={menuOptions}
								iconComponent={<Menu fontSize="inherit" />}
							/>
						</button>
					</div>
				</div>
			</th>
		</>
	);
};
